import { useAuthReady } from './useAuthReady';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWallet } from './useWallet';
import { buyPresale, fetchPresale, type PresaleOverview } from '../lib/presale';
import { buildIncptPriceHistory } from '@incpt/shared';
import { holdings, mockPresale, MOCK_ROUND, MOCK_STATS } from '../lib/presale.mock';

/** Set `VITE_PRESALE_MOCK=1` to walk the UI without `/v1/presale`. */
export const PRESALE_MOCK = import.meta.env.VITE_PRESALE_MOCK === '1';

const PRESALE_KEY = ['presale'] as const;
const MOCK_LATENCY_MS = 500;
const settle = () => new Promise((r) => setTimeout(r, MOCK_LATENCY_MS));

export function usePresale() {
  const { canFetch, initData } = useAuthReady();
  const { byTokenChain } = useWallet();
  const ready = PRESALE_MOCK || canFetch;

  const q = useQuery({
    queryKey: PRESALE_KEY,
    enabled: ready,
    staleTime: 5_000,
    refetchOnMount: 'always',
    queryFn: () => (PRESALE_MOCK ? Promise.resolve(mockPresale()) : fetchPresale(initData)),
  });

  const visible =
    q.data?.visible ??
    // Older payloads / partial responses: fall back to round.open.
    q.data?.round.open ??
    PRESALE_MOCK;

  const round = q.data?.round ?? MOCK_ROUND;
  const fromApi = q.data?.priceHistory;
  const priceHistory =
    fromApi && fromApi.length >= 2
      ? fromApi
      : buildIncptPriceHistory(round.marketPrice);

  return {
    visible,
    round,
    purchases: q.data?.purchases ?? [],
    balance: q.data?.balance ?? '0',
    priceHistory,
    stats: q.data?.stats ?? MOCK_STATS,
    /** TRC-20 USDT available for payment. */
    usdtBalance: byTokenChain['USDT:trx'] ?? 0,
    loading: q.isLoading,
    error: q.isError,
  };
}

/** Credit a purchase after the USDT payment transfer has settled. */
export function usePresaleBuy() {
  const { initData } = useAuthReady();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paymentTransferId,
      amount,
      tokens,
      price,
    }: {
      paymentTransferId: string;
      amount: number;
      tokens: number;
      price: number;
    }) => {
      if (!PRESALE_MOCK) {
        const { purchase } = await buyPresale(
          initData,
          { payment_transfer_id: paymentTransferId },
          crypto.randomUUID(),
        );
        return purchase;
      }
      await settle();
      return {
        id: `ps-${Date.now()}`,
        amount,
        tokens,
        price,
        createdAt: new Date().toISOString(),
        status: 'settled' as const,
      };
    },
    onSuccess: (purchase) => {
      if (!PRESALE_MOCK) {
        void qc.invalidateQueries({ queryKey: PRESALE_KEY });
        void qc.invalidateQueries({ queryKey: ['balances'] });
        void qc.invalidateQueries({ queryKey: ['transfers'] });
        return;
      }
      qc.setQueryData<PresaleOverview>(PRESALE_KEY, (prev) => {
        if (!prev) return prev;
        const purchases = [purchase, ...prev.purchases];
        return { ...prev, purchases, balance: String(holdings(purchases)) };
      });
    },
  });
}
